import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import homeApiService from '../services/homeApiService';
import './HomePage.css';

const ServerInfo = [
  { id: 2, name: 'RCP Jaipur', serverIP: '10.141.61.40' },
  { id: 3, name: 'HDR 1101', serverIP: '10.133.100.21' },
  { id: 4, name: 'HDR 3000', serverIP: '10.134.1.21' },
  { id: 5, name: 'HDR 1100', serverIP: '10.133.1.22' },
  { id: 6, name: 'Guwahati', serverIP: '10.161.1.22' },
];

const ShiftOptions = ['A', 'B'];
const AreaOptions = ['PCB', 'EMA'];

// ─── ONLY NEW CODE: sessionStorage helpers ────────────────────────────────────
const STORAGE_KEY = 'homePageSelections';

const saveSelections = (state) => {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) {}
};

const loadSelections = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
};

const clearSelections = () => {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
};
// ─────────────────────────────────────────────────────────────────────────────

const HomePage = () => {
  const navigate = useNavigate();

  // ── ONLY CHANGE HERE: read saved state on first render ───────────────────
  const saved = loadSelections();

  // All useState calls are identical to original, just seeded from saved if available
  const [viewMode, setViewMode]               = useState(saved?.viewMode       ?? 'live');
  const [startDate, setStartDate]             = useState(saved?.startDate      ?? '');
  const [endDate, setEndDate]                 = useState(saved?.endDate        ?? '');
  const [loading, setLoading]                 = useState(false);
  const [currentStep, setCurrentStep]         = useState(saved?.currentStep    ?? 0);
  const [dateError, setDateError]             = useState('');

  const [selectedPlant,   setSelectedPlant]   = useState(saved?.selectedPlant   ?? null);
  const [selectedShift,   setSelectedShift]   = useState(saved?.selectedShift   ?? null);
  const [selectedArea,    setSelectedArea]     = useState(saved?.selectedArea    ?? null);
  const [selectedProject, setSelectedProject] = useState(saved?.selectedProject ?? null);
  const [selectedLine,    setSelectedLine]     = useState(saved?.selectedLine    ?? null);

  const [projects,           setProjects]           = useState([]);
  const [projectsWithStatus, setProjectsWithStatus] = useState([]);
  const [lines,              setLines]              = useState([]);
  const [searchTerm,         setSearchTerm]         = useState('');
  const [lineSearchTerm,     setLineSearchTerm]     = useState('');

  // Refs for auto-scroll — unchanged
  const plantRef    = useRef(null);
  const shiftAreaRef = useRef(null);
  const projectRef  = useRef(null);
  const lineRef     = useRef(null);

  const steps = [
    { id: 1, name: 'Plant',   icon: '🏭', status: selectedPlant   ? 'complete' : currentStep === 0 ? 'current' : 'upcoming' },
    { id: 2, name: 'Shift',   icon: '🕐', status: selectedShift   ? 'complete' : currentStep === 1 ? 'current' : 'upcoming' },
    { id: 3, name: 'Area',    icon: '📍', status: selectedArea    ? 'complete' : currentStep === 2 ? 'current' : 'upcoming' },
    { id: 4, name: 'Project', icon: '📋', status: selectedProject ? 'complete' : currentStep === 3 ? 'current' : 'upcoming' },
    { id: 5, name: 'Line',    icon: '⚙️', status: selectedLine   ? 'complete' : currentStep === 4 ? 'current' : 'upcoming' },
  ];

  const scrollToSection = (ref) => {
    if (ref?.current) {
      setTimeout(() => ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  // ── ONLY NEW useEffect: persist selections whenever they change ───────────
  useEffect(() => {
    saveSelections({
      viewMode,
      startDate,
      endDate,
      currentStep,
      selectedPlant,
      selectedShift,
      selectedArea,
      selectedProject,
      selectedLine,
    });
  }, [viewMode, startDate, endDate, currentStep,
      selectedPlant, selectedShift, selectedArea, selectedProject, selectedLine]);

  // ── ALL ORIGINAL useEffects — completely unchanged ────────────────────────
  useEffect(() => {
    if (viewMode === 'live') {
      const today = new Date().toISOString().split('T')[0];
      setStartDate(today);
      setEndDate(today);
      setDateError('');
    } else {
      // only clear dates if there is no saved state (i.e. fresh load, not restore)
      if (!saved) {
        setStartDate('');
        setEndDate('');
      }
    }
  }, [viewMode]);

  useEffect(() => {
    if (selectedArea && selectedPlant && startDate) {
      fetchProjects();
    }
  }, [selectedArea, selectedPlant, startDate]);

  useEffect(() => {
    if (selectedProject && selectedShift && startDate && endDate) {
      fetchLines();
    }
  }, [selectedProject, selectedShift, startDate, endDate]);

  // ── ALL ORIGINAL functions — completely unchanged ─────────────────────────
  const validateDates = (start, end) => {
    if (!start || !end) { setDateError(''); return true; }

    const startDateObj = new Date(start); startDateObj.setHours(0,0,0,0);
    const endDateObj   = new Date(end);   endDateObj.setHours(0,0,0,0);
    const today        = new Date();      today.setHours(0,0,0,0);

    if (endDateObj < startDateObj) {
      setDateError('End date cannot be before start date');
      return false;
    }
    if (startDateObj > today || endDateObj > today) {
      setDateError('Dates cannot be in the future');
      return false;
    }
    const daysDiff = Math.floor((endDateObj - startDateObj) / (1000*60*60*24)) + 1;
    if (daysDiff > 31) {
      setDateError('Date range cannot exceed 31 days');
      return false;
    }
    setDateError('');
    return true;
  };

  const handleStartDateChange = useCallback((e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    if (viewMode === 'datewise' && newStartDate) {
      setEndDate(newStartDate);
      validateDates(newStartDate, newStartDate);
      setTimeout(() => scrollToSection(plantRef), 100);
    } else {
      validateDates(newStartDate, endDate);
    }
  }, [viewMode, endDate]);

  const handleEndDateChange = useCallback((e) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);
    validateDates(startDate, newEndDate);
    if (viewMode === 'datewise' && newEndDate && startDate) {
      setTimeout(() => scrollToSection(plantRef), 100);
    }
  }, [viewMode, startDate]);

  // ── fetchProjects — IDENTICAL to original ────────────────────────────────
  const fetchProjects = async () => {
    setLoading(true);
    try {
      const dateToUse = viewMode === 'live'
        ? new Date().toISOString().split('T')[0]
        : startDate;

      const result = await homeApiService.getProjects(
        selectedArea,
        selectedPlant.id,
        dateToUse
      );

      const projectList = Array.isArray(result) ? result : [];
      setProjects(projectList);
      await fetchProjectsProductionStatus(projectList);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
      setProjectsWithStatus([]);
      alert(`Error fetching projects: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── fetchProjectsProductionStatus — IDENTICAL to original ────────────────
  const fetchProjectsProductionStatus = async (projectList) => {
    if (!projectList || projectList.length === 0) {
      setProjectsWithStatus([]);
      return;
    }

    try {
      const projectsWithProductionStatus = await Promise.all(
        projectList.map(async (project) => {
          try {
            const projectCode = project.projCode || project.projectCode || project.code || project.id;

            let lines = [];
            if (selectedArea === 'PCB') {
              lines = await homeApiService.getPCBRunningLines(selectedPlant.id, projectCode, startDate, endDate);
            } else if (selectedArea === 'EMA') {
              lines = await homeApiService.getEMARunningLines(selectedPlant.id, projectCode, startDate, endDate);
            }

            let hasProduction = false;
            if (Array.isArray(lines) && lines.length > 0) {
              for (const line of lines.slice(0, 3)) {
                const production = await homeApiService.getHourlyProduction(
                  selectedPlant.id,
                  projectCode,
                  line.userId,
                  selectedShift || 'A',
                  startDate,
                  endDate,
                  selectedArea
                );
                if (production > 0) { hasProduction = true; break; }
              }
            }

            return { ...project, hasProduction };
          } catch (err) {
            console.error(`Error checking production for project ${project.projCode}:`, err);
            return { ...project, hasProduction: false };
          }
        })
      );
      setProjectsWithStatus(projectsWithProductionStatus);
    } catch (error) {
      console.error('Error fetching project production status:', error);
      setProjectsWithStatus(projectList.map(p => ({ ...p, hasProduction: false })));
    }
  };

  // ── fetchLines — IDENTICAL to original ───────────────────────────────────
  const fetchLines = async () => {
    setLoading(true);
    try {
      const projectCode = selectedProject.projCode || selectedProject.projectCode || selectedProject.code || selectedProject.id;

      const result = await homeApiService.getLineNumbers(
        projectCode,
        selectedShift,
        selectedArea,
        selectedPlant.id,
        startDate,
        endDate
      );

      if (Array.isArray(result) && result.length > 0) {
        setLines(result);
      } else {
        setLines([]);
      }
    } catch (error) {
      console.error('Error fetching lines:', error);
      setLines([]);
      alert(`Error fetching lines: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── ALL ORIGINAL handlers — completely unchanged ───────────────────────────
  const handlePlantSelect = (plant) => {
    setSelectedPlant(plant);
    setCurrentStep(1);
    setSelectedShift(null);
    setSelectedArea(null);
    setSelectedProject(null);
    setSelectedLine(null);
    setProjects([]);
    setProjectsWithStatus([]);
    setLines([]);
    scrollToSection(shiftAreaRef);
  };

  const handleShiftSelect = (shift) => {
    setSelectedShift(shift);
    setSelectedArea(null);
    setSelectedProject(null);
    setSelectedLine(null);
    setProjects([]);
    setProjectsWithStatus([]);
    setLines([]);
    setCurrentStep(2);
  };

  const handleAreaSelect = (area) => {
    setSelectedArea(area);
    setSelectedProject(null);
    setSelectedLine(null);
    setProjects([]);
    setProjectsWithStatus([]);
    setLines([]);
    setCurrentStep(3);
    scrollToSection(projectRef);
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setCurrentStep(4);
    setSelectedLine(null);
    scrollToSection(lineRef);
  };

  const handleLineSelect = (line) => {
    setSelectedLine(line);
  };

  const handleFind = () => {
    if (!selectedPlant || !selectedShift || !selectedArea || !selectedProject || !selectedLine) {
      alert('Please select all parameters');
      return;
    }
    if (!validateDates(startDate, endDate)) {
      alert('Please fix the date validation errors');
      return;
    }

    const params = {
      plantId:     selectedPlant.id,
      plantName:   selectedPlant.name,
      shift:       selectedShift,
      area:        selectedArea,
      projectId:   selectedProject.projCode || selectedProject.projectCode || selectedProject.id,
      projectName: selectedProject.projName || selectedProject.projectName || selectedProject.name,
      lineNo:      selectedLine.userId,
      lineName:    selectedLine.lineNo,
      userName:    selectedLine.userName,
      startDate,
      endDate,
      viewMode,
    };

    sessionStorage.setItem('dashboardParams', JSON.stringify(params));
    // homePageSelections is intentionally NOT cleared here — we want it to persist
    navigate('/dashboard');
  };

  // ── ONLY CHANGE: clearSelections() added alongside original reset logic ───
  const handleClearAll = () => {
    clearSelections(); // ← only new line
    setSelectedPlant(null);
    setSelectedShift(null);
    setSelectedArea(null);
    setSelectedProject(null);
    setSelectedLine(null);
    setProjects([]);
    setProjectsWithStatus([]);
    setLines([]);
    setCurrentStep(0);
    setSearchTerm('');
    setLineSearchTerm('');
    setDateError('');
    if (viewMode === 'datewise') {
      setStartDate('');
      setEndDate('');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Derived display data — unchanged ─────────────────────────────────────
  const displayProjects = projectsWithStatus.length > 0 ? projectsWithStatus : projects;

  const filteredProjects = displayProjects.filter(project => {
    const projectName = project.projName || project.projectName || project.name || '';
    return projectName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const filteredLines = lines.filter(line => {
    const searchStr = lineSearchTerm.toLowerCase();
    return (
      line.lineNo?.toLowerCase().includes(searchStr) ||
      line.userName?.toLowerCase().includes(searchStr) ||
      line.userId?.toLowerCase().includes(searchStr)
    );
  });

  const getDisplayValue = (item, ...keys) => {
    if (!item) return '';
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    if (typeof item === 'object' && item !== null) {
      for (const key of keys) {
        if (item[key] !== undefined && item[key] !== null && item[key] !== '') return String(item[key]);
      }
    }
    return '';
  };

  const today = new Date().toISOString().split('T')[0];

  // ── JSX — IDENTICAL to original ──────────────────────────────────────────
  return (
    <div className="modern-home-container">
      {/* Header */}
      <div className="modern-header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="header-title">
              <span className="header-icon">📊</span>
              Production Monitoring System
            </h1>
          </div>
          <div className="mode-badge">
            <div className="mode-badge-label">Current Mode</div>
            <div className="mode-badge-value">
              <span className={`mode-indicator ${viewMode === 'live' ? 'live' : 'historical'}`}>
                {viewMode === 'live' ? '🔴' : '📅'}
              </span>
              {viewMode === 'live' ? 'Live Monitoring' : 'Historical Data'}
            </div>
          </div>
        </div>
      </div>

      <div className="modern-content">
        {/* Mode Toggle & Date Selection */}
        <div className="control-card">
          <div className="control-row">
            <div className="mode-toggle-section">
              <label className="control-label">Select Mode</label>
              <div className="mode-toggle">
                <button
                  onClick={() => setViewMode('live')}
                  className={`mode-btn ${viewMode === 'live' ? 'active live' : ''}`}
                >
                  <span className="mode-btn-icon">🔴</span>
                  Live Mode
                </button>
                <button
                  onClick={() => setViewMode('datewise')}
                  className={`mode-btn ${viewMode === 'datewise' ? 'active datewise' : ''}`}
                >
                  <span className="mode-btn-icon">📅</span>
                  Date Wise
                </button>
              </div>
            </div>

            <div className="date-inputs">
              <div className="date-input-group">
                <label className="control-label">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  disabled={viewMode === 'live'}
                  max={today}
                  className="modern-date-input"
                />
              </div>
              <div className="date-input-group">
                <label className="control-label">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  disabled={viewMode === 'live'}
                  min={startDate}
                  max={today}
                  className="modern-date-input"
                />
              </div>
            </div>

            {(selectedPlant || selectedShift || selectedArea || selectedProject || selectedLine) && (
              <button onClick={handleClearAll} className="clear-all-btn">
                <span className="clear-icon">✕</span>
                Clear All
              </button>
            )}
          </div>
          {dateError && <div className="date-error">{dateError}</div>}
        </div>

        {/* Progress Stepper */}
        <div className="stepper-card">
          <div className="stepper-container">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="step-item">
                  <div className={`step-circle ${step.status}`}>
                    {step.status === 'complete' ? '✓' : step.icon}
                  </div>
                  <div className={`step-label ${step.status}`}>{step.name}</div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`step-line ${step.status === 'complete' ? 'complete' : ''}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Single Column Layout */}
        <div className="selection-area">
          {/* Plant Selection */}
          <div ref={plantRef} className="selection-card plant-card">
            <div className="card-header">
              <div className="card-icon plant-icon">🏭</div>
              <div className="card-title-group">
                <h3 className="card-title">Select Plant</h3>
                <p className="card-subtitle">Choose your production facility</p>
              </div>
            </div>
            <div className="options-grid">
              {ServerInfo.map((plant) => (
                <button
                  key={plant.id}
                  onClick={() => handlePlantSelect(plant)}
                  className={`option-item ${selectedPlant?.id === plant.id ? 'selected' : ''}`}
                >
                  <div className="option-name">{plant.name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* SHIFT & AREA SIDE BY SIDE */}
          {selectedPlant && (
            <div ref={shiftAreaRef} className="shift-area-wrapper">
              <div className="selection-card shift-card-side">
                <div className="card-header">
                  <div className="card-icon shift-icon">🕐</div>
                  <div className="card-title-group">
                    <h3 className="card-title">Select Shift</h3>
                    <p className="card-subtitle">Choose working shift</p>
                  </div>
                </div>
                <div className="shift-options">
                  {ShiftOptions.map((shift) => (
                    <button
                      key={shift}
                      onClick={() => handleShiftSelect(shift)}
                      className={`shift-btn ${selectedShift === shift ? 'selected' : ''}`}
                    >
                      Shift {shift}
                    </button>
                  ))}
                </div>
              </div>

              {selectedShift && (
                <div className="selection-card area-card-side fade-in">
                  <div className="card-header">
                    <div className="card-icon area-icon">📍</div>
                    <div className="card-title-group">
                      <h3 className="card-title">Select Area</h3>
                      <p className="card-subtitle">Choose production area</p>
                    </div>
                  </div>
                  <div className="area-options">
                    {AreaOptions.map((area) => (
                      <button
                        key={area}
                        onClick={() => handleAreaSelect(area)}
                        className={`area-btn ${selectedArea === area ? 'selected' : ''}`}
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Project Selection */}
          {selectedArea && selectedShift && (
            <div ref={projectRef} className="selection-card project-card fade-in">
              <div className="card-header">
                <div className="card-icon project-icon">📋</div>
                <div className="card-title-group">
                  <h3 className="card-title">Select Project</h3>
                  <p className="card-subtitle">Choose your project</p>
                </div>
              </div>

              {loading && !projects.length ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <span>Loading projects...</span>
                </div>
              ) : (
                <>
                  {projects.length > 3 && (
                    <div className="search-box">
                      <span className="search-icon">🔍</span>
                      <input
                        type="text"
                        placeholder="Search projects..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                      />
                    </div>
                  )}
                  <div className="project-grid">
                    {filteredProjects.length > 0 ? (
                      filteredProjects.map((project, index) => (
                        <button
                          key={index}
                          onClick={() => handleProjectSelect(project)}
                          className={`project-item ${selectedProject === project ? 'selected' : ''}`}
                        >
                          <div className="project-name">
                            {getDisplayValue(project, 'projName', 'projectName', 'name')}
                          </div>
                          <div className="project-footer">
                            <div className="project-code">
                              {getDisplayValue(project, 'projCode', 'projectCode', 'code')}
                            </div>
                            <div
                              className={`project-status-indicator ${project.hasProduction ? 'active' : 'inactive'}`}
                              title={project.hasProduction ? 'Active Production' : 'No Production'}
                            />
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="empty-state">
                        <span className="empty-icon">📋</span>
                        <p>No projects found</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Line Selection */}
          {selectedProject && (
            <div ref={lineRef} className="selection-card line-card fade-in">
              <div className="card-header">
                <div className="card-icon line-icon">⚙️</div>
                <div className="card-title-group">
                  <h3 className="card-title">Select Production Line</h3>
                  <p className="card-subtitle">Choose your production line</p>
                </div>
              </div>

              {loading && !lines.length ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <span>Loading lines...</span>
                </div>
              ) : (
                <>
                  {lines.length > 3 && (
                    <div className="search-box">
                      <span className="search-icon">🔍</span>
                      <input
                        type="text"
                        placeholder="Search lines..."
                        value={lineSearchTerm}
                        onChange={(e) => setLineSearchTerm(e.target.value)}
                        className="search-input"
                      />
                    </div>
                  )}
                  <div className="line-grid">
                    {filteredLines.length > 0 ? (
                      filteredLines.map((line) => (
                        <button
                          key={line.userId}
                          onClick={() => handleLineSelect(line)}
                          className={`line-item ${selectedLine?.userId === line.userId ? 'selected' : ''}`}
                        >
                          <div className="line-header">
                            <div className="line-info">
                              <div className="line-number">{line.lineNo}</div>
                            </div>
                            <div className="line-status-dot"></div>
                          </div>
                          <div className="line-stats">
                            <div className="line-stat">
                              <div className="stat-label">USER ID</div>
                              <div className="stat-value user-id">{line.userId}</div>
                            </div>
                            <div className="line-stat">
                              <div className="stat-label">PRODUCTION</div>
                              <div className="stat-value production">
                                <span className="trend-icon">📈</span>
                                {line.totalProduction}
                              </div>
                            </div>
                          </div>
                          <div className="line-sync">
                            <span className="sync-icon">🕐</span>
                            {line.lastSynced}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="empty-state">
                        <span className="empty-icon">⚙️</span>
                        <p>No lines available</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* View Dashboard Button */}
          {selectedLine && (
            <div className="dashboard-action-card fade-in">
              <button
                onClick={handleFind}
                className="dashboard-btn-large"
                disabled={!!dateError}
              >
                <span className="btn-icon">⚡</span>
                View Dashboard
                <span className="btn-arrow">→</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;


//----
// import React, { useState, useEffect, useRef, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom';
// import homeApiService from '../services/homeApiService';
// import './HomePage.css';

// const ServerInfo = [
//   { id: 2, name: 'RCP Jaipur', serverIP: '10.141.61.40' },
//   { id: 3, name: 'HDR 1101', serverIP: '10.133.100.21' },
//   { id: 4, name: 'HDR 3000', serverIP: '10.134.1.21' },
//   { id: 5, name: 'HDR 1100', serverIP: '10.133.1.22' },
//   { id: 6, name: 'Guhawati', serverIP: '10.161.1.22' },
// ];

// const ShiftOptions = ['A', 'B'];
// const AreaOptions = ['PCB', 'EMA'];

// const HomePage = () => {
//   const navigate = useNavigate();
//   const [viewMode, setViewMode] = useState('live');
//   const [startDate, setStartDate] = useState('');
//   const [endDate, setEndDate] = useState('');
//   const [loading, setLoading] = useState(false);
//   const [currentStep, setCurrentStep] = useState(0);
//   const [dateError, setDateError] = useState('');
  
//   const [selectedPlant, setSelectedPlant] = useState(null);
//   const [selectedShift, setSelectedShift] = useState(null);
//   const [selectedArea, setSelectedArea] = useState(null);
//   const [selectedProject, setSelectedProject] = useState(null);
//   const [selectedLine, setSelectedLine] = useState(null);
  
//   const [projects, setProjects] = useState([]);
//   const [projectsWithStatus, setProjectsWithStatus] = useState([]);
//   const [lines, setLines] = useState([]);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [lineSearchTerm, setLineSearchTerm] = useState('');

//   // Refs for auto-scroll
//   const plantRef = useRef(null);
//   const shiftAreaRef = useRef(null);
//   const projectRef = useRef(null);
//   const lineRef = useRef(null);

//   const steps = [
//     { id: 1, name: 'Plant', icon: '🏭', status: selectedPlant ? 'complete' : currentStep === 0 ? 'current' : 'upcoming' },
//     { id: 2, name: 'Shift', icon: '🕐', status: selectedShift ? 'complete' : currentStep === 1 ? 'current' : 'upcoming' },
//     { id: 3, name: 'Area', icon: '📍', status: selectedArea ? 'complete' : currentStep === 2 ? 'current' : 'upcoming' },
//     { id: 4, name: 'Project', icon: '📋', status: selectedProject ? 'complete' : currentStep === 3 ? 'current' : 'upcoming' },
//     { id: 5, name: 'Line', icon: '⚙️', status: selectedLine ? 'complete' : currentStep === 4 ? 'current' : 'upcoming' },
//   ];

//   const scrollToSection = (ref) => {
//     if (ref && ref.current) {
//       setTimeout(() => {
//         ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
//       }, 100);
//     }
//   };

//   useEffect(() => {
//     if (viewMode === 'live') {
//       const today = new Date().toISOString().split('T')[0];
//       setStartDate(today);
//       setEndDate(today);
//       setDateError('');
//     } else {
//       setStartDate('');
//       setEndDate('');
//     }
//   }, [viewMode]);

//   useEffect(() => {
//     if (selectedArea && selectedPlant && startDate) {
//       fetchProjects();
//     }
//   }, [selectedArea, selectedPlant, startDate]);

//   useEffect(() => {
//     if (selectedProject && selectedShift && startDate && endDate) {
//       fetchLines();
//     }
//   }, [selectedProject, selectedShift, startDate, endDate]);

//   const validateDates = (start, end) => {
//     if (!start || !end) {
//       setDateError('');
//       return true;
//     }

//     const startDateObj = new Date(start);
//     const endDateObj = new Date(end);

//     startDateObj.setHours(0, 0, 0, 0);
//     endDateObj.setHours(0, 0, 0, 0);

//     const today = new Date();
//     today.setHours(0, 0, 0, 0);

//     if (endDateObj < startDateObj) {
//       setDateError('End date cannot be before start date');
//       return false;
//     }

//     if (startDateObj > today || endDateObj > today) {
//       setDateError('Dates cannot be in the future');
//       return false;
//     }

//     const daysDiff = Math.floor((endDateObj - startDateObj) / (1000 * 60 * 60 * 24)) + 1;

//     if (daysDiff > 31) {
//       setDateError('Date range cannot exceed 31 days');
//       return false;
//     }

//     setDateError('');
//     return true;
//   };

//   const handleStartDateChange = useCallback((e) => {
//     const newStartDate = e.target.value;
//     setStartDate(newStartDate);
    
//     if (viewMode === 'datewise' && newStartDate) {
//       setEndDate(newStartDate);
//       validateDates(newStartDate, newStartDate);
      
//       setTimeout(() => {
//         scrollToSection(plantRef);
//       }, 100);
//     } else {
//       validateDates(newStartDate, endDate);
//     }
//   }, [viewMode, endDate]);

//   const handleEndDateChange = useCallback((e) => {
//     const newEndDate = e.target.value;
//     setEndDate(newEndDate);
//     validateDates(startDate, newEndDate);
    
//     if (viewMode === 'datewise' && newEndDate && startDate) {
//       setTimeout(() => {
//         scrollToSection(plantRef);
//       }, 100);
//     }
//   }, [viewMode, startDate]);

//   const fetchProjects = async () => {
//     setLoading(true);
//     try {
//       const dateToUse = viewMode === 'live' ? new Date().toISOString().split('T')[0] : startDate;

//       const result = await homeApiService.getProjects(
//         selectedArea,
//         selectedPlant.id,
//         dateToUse
//       );

//       const projectList = Array.isArray(result) ? result : [];
//       setProjects(projectList);
      
//       await fetchProjectsProductionStatus(projectList);
//     } catch (error) {
//       console.error('Error fetching projects:', error);
//       setProjects([]);
//       setProjectsWithStatus([]);
//       alert(`Error fetching projects: ${error.message}`);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchProjectsProductionStatus = async (projectList) => {
//     if (!projectList || projectList.length === 0) {
//       setProjectsWithStatus([]);
//       return;
//     }

//     try {
//       const projectsWithProductionStatus = await Promise.all(
//         projectList.map(async (project) => {
//           try {
//             const projectCode = project.projCode || project.projectCode || project.code || project.id;
            
//             let lines = [];
//             if (selectedArea === 'PCB') {
//               lines = await homeApiService.getPCBRunningLines(selectedPlant.id, projectCode, startDate, endDate);
//             } else if (selectedArea === 'EMA') {
//               lines = await homeApiService.getEMARunningLines(selectedPlant.id, projectCode, startDate, endDate);
//             }

//             let hasProduction = false;
//             if (Array.isArray(lines) && lines.length > 0) {
//               for (const line of lines.slice(0, 3)) {
//                 const production = await homeApiService.getHourlyProduction(
//                   selectedPlant.id,
//                   projectCode,
//                   line.userId,
//                   selectedShift || 'A',
//                   startDate,
//                   endDate,
//                   selectedArea
//                 );
                
//                 if (production > 0) {
//                   hasProduction = true;
//                   break;
//                 }
//               }
//             }

//             return {
//               ...project,
//               hasProduction
//             };
//           } catch (err) {
//             console.error(`Error checking production for project ${project.projCode}:`, err);
//             return {
//               ...project,
//               hasProduction: false
//             };
//           }
//         })
//       );

//       setProjectsWithStatus(projectsWithProductionStatus);
//     } catch (error) {
//       console.error('Error fetching project production status:', error);
//       setProjectsWithStatus(projectList.map(p => ({ ...p, hasProduction: false })));
//     }
//   };

//   const fetchLines = async () => {
//     setLoading(true);
//     try {
//       const projectCode = selectedProject.projCode || selectedProject.projectCode || selectedProject.code || selectedProject.id;
      
//       const result = await homeApiService.getLineNumbers(
//         projectCode,
//         selectedShift,
//         selectedArea,
//         selectedPlant.id,
//         startDate,
//         endDate
//       );
      
//       if (Array.isArray(result) && result.length > 0) {
//         setLines(result);
//       } else {
//         setLines([]);
//       }
//     } catch (error) {
//       console.error('Error fetching lines:', error);
//       setLines([]);
//       alert(`Error fetching lines: ${error.message}`);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handlePlantSelect = (plant) => {
//     setSelectedPlant(plant);
//     setCurrentStep(1);
//     setSelectedShift(null);
//     setSelectedArea(null);
//     setSelectedProject(null);
//     setSelectedLine(null);
//     setProjects([]);
//     setProjectsWithStatus([]);
//     setLines([]);
//     scrollToSection(shiftAreaRef);
//   };

//   const handleShiftSelect = (shift) => {
//     setSelectedShift(shift);
//     setSelectedArea(null);
//     setSelectedProject(null);
//     setSelectedLine(null);
//     setProjects([]);
//     setProjectsWithStatus([]);
//     setLines([]);
//     setCurrentStep(2);
//   };

//   const handleAreaSelect = (area) => {
//     setSelectedArea(area);
//     setSelectedProject(null);
//     setSelectedLine(null);
//     setProjects([]);
//     setProjectsWithStatus([]);
//     setLines([]);
//     setCurrentStep(3);
//     scrollToSection(projectRef);
//   };

//   const handleProjectSelect = (project) => {
//     setSelectedProject(project);
//     setCurrentStep(4);
//     setSelectedLine(null);
//     scrollToSection(lineRef);
//   };

//   const handleLineSelect = (line) => {
//     setSelectedLine(line);
//   };

//   const handleFind = () => {
//     if (!selectedPlant || !selectedShift || !selectedArea || !selectedProject || !selectedLine) {
//       alert('Please select all parameters');
//       return;
//     }

//     if (!validateDates(startDate, endDate)) {
//       alert('Please fix the date validation errors');
//       return;
//     }

//     const params = {
//       plantId: selectedPlant.id,
//       plantName: selectedPlant.name,
//       shift: selectedShift,
//       area: selectedArea,
//       projectId: selectedProject.projCode || selectedProject.projectCode || selectedProject.id,
//       projectName: selectedProject.projName || selectedProject.projectName || selectedProject.name,
//       lineNo: selectedLine.userId,
//       lineName: selectedLine.lineNo,
//       userName: selectedLine.userName,
//       startDate,
//       endDate,
//       viewMode
//     };

//     sessionStorage.setItem('dashboardParams', JSON.stringify(params));
//     navigate('/dashboard');
//   };

//   const handleClearAll = () => {
//     setSelectedPlant(null);
//     setSelectedShift(null);
//     setSelectedArea(null);
//     setSelectedProject(null);
//     setSelectedLine(null);
//     setProjects([]);
//     setProjectsWithStatus([]);
//     setLines([]);
//     setCurrentStep(0);
//     setSearchTerm('');
//     setLineSearchTerm('');
//     setDateError('');
    
//     if (viewMode === 'datewise') {
//       setStartDate('');
//       setEndDate('');
//     }
    
//     window.scrollTo({ top: 0, behavior: 'smooth' });
//   };

//   const displayProjects = projectsWithStatus.length > 0 ? projectsWithStatus : projects;

//   const filteredProjects = displayProjects.filter(project => {
//     const projectName = project.projName || project.projectName || project.name || '';
//     return projectName.toLowerCase().includes(searchTerm.toLowerCase());
//   });

//   const filteredLines = lines.filter(line => {
//     const searchStr = lineSearchTerm.toLowerCase();
//     return (
//       line.lineNo?.toLowerCase().includes(searchStr) ||
//       line.userName?.toLowerCase().includes(searchStr) ||
//       line.userId?.toLowerCase().includes(searchStr)
//     );
//   });

//   const getDisplayValue = (item, ...keys) => {
//     if (!item) return '';
//     if (typeof item === 'string' || typeof item === 'number') {
//       return String(item);
//     }
//     if (typeof item === 'object' && item !== null) {
//       for (const key of keys) {
//         if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
//           return String(item[key]);
//         }
//       }
//     }
//     return '';
//   };

//   const today = new Date().toISOString().split('T')[0];

//   return (
//     <div className="modern-home-container">
//       {/* Header */}
//       <div className="modern-header">
//         <div className="header-content">
//           <div className="header-left">
//             <h1 className="header-title">
//               <span className="header-icon">📊</span>
//               Production Monitoring System
//             </h1>
//           </div>
//           <div className="mode-badge">
//             <div className="mode-badge-label">Current Mode</div>
//             <div className="mode-badge-value">
//               <span className={`mode-indicator ${viewMode === 'live' ? 'live' : 'historical'}`}>
//                 {viewMode === 'live' ? '🔴' : '📅'}
//               </span>
//               {viewMode === 'live' ? 'Live Monitoring' : 'Historical Data'}
//             </div>
//           </div>
//         </div>
//       </div>

//       <div className="modern-content">
//         {/* Mode Toggle & Date Selection */}
//         <div className="control-card">
//           <div className="control-row">
//             <div className="mode-toggle-section">
//               <label className="control-label">Select Mode</label>
//               <div className="mode-toggle">
//                 <button
//                   onClick={() => setViewMode('live')}
//                   className={`mode-btn ${viewMode === 'live' ? 'active live' : ''}`}
//                 >
//                   <span className="mode-btn-icon">🔴</span>
//                   Live Mode
//                 </button>
//                 <button
//                   onClick={() => setViewMode('datewise')}
//                   className={`mode-btn ${viewMode === 'datewise' ? 'active datewise' : ''}`}
//                 >
//                   <span className="mode-btn-icon">📅</span>
//                   Date Wise
//                 </button>
//               </div>
//             </div>

//             <div className="date-inputs">
//               <div className="date-input-group">
//                 <label className="control-label">Start Date</label>
//                 <input
//                   type="date"
//                   value={startDate}
//                   onChange={handleStartDateChange}
//                   disabled={viewMode === 'live'}
//                   max={today}
//                   className="modern-date-input"
//                 />
//               </div>
//               <div className="date-input-group">
//                 <label className="control-label">End Date</label>
//                 <input
//                   type="date"
//                   value={endDate}
//                   onChange={handleEndDateChange}
//                   disabled={viewMode === 'live'}
//                   min={startDate}
//                   max={today}
//                   className="modern-date-input"
//                 />
//               </div>
//             </div>

//             {(selectedPlant || selectedShift || selectedArea || selectedProject || selectedLine) && (
//               <button onClick={handleClearAll} className="clear-all-btn">
//                 <span className="clear-icon">✕</span>
//                 Clear All
//               </button>
//             )}
//           </div>
//           {dateError && <div className="date-error">{dateError}</div>}
//         </div>

//         {/* Progress Stepper */}
//         <div className="stepper-card">
//           <div className="stepper-container">
//             {steps.map((step, index) => (
//               <React.Fragment key={step.id}>
//                 <div className="step-item">
//                   <div className={`step-circle ${step.status}`}>
//                     {step.status === 'complete' ? '✓' : step.icon}
//                   </div>
//                   <div className={`step-label ${step.status}`}>{step.name}</div>
//                 </div>
//                 {index < steps.length - 1 && (
//                   <div className={`step-line ${step.status === 'complete' ? 'complete' : ''}`} />
//                 )}
//               </React.Fragment>
//             ))}
//           </div>
//         </div>

//         {/* Single Column Layout */}
//         <div className="selection-area">
//           {/* Plant Selection */}
//           <div ref={plantRef} className="selection-card plant-card">
//             <div className="card-header">
//               <div className="card-icon plant-icon">🏭</div>
//               <div className="card-title-group">
//                 <h3 className="card-title">Select Plant</h3>
//                 <p className="card-subtitle">Choose your production facility</p>
//               </div>
//             </div>
//             <div className="options-grid">
//               {ServerInfo.map((plant) => (
//                 <button
//                   key={plant.id}
//                   onClick={() => handlePlantSelect(plant)}
//                   className={`option-item ${selectedPlant?.id === plant.id ? 'selected' : ''}`}
//                 >
//                   <div className="option-name">{plant.name}</div>
//                 </button>
//               ))}
//             </div>
//           </div>

//           {/* SHIFT & AREA SIDE BY SIDE */}
//           {selectedPlant && (
//             <div ref={shiftAreaRef} className="shift-area-wrapper">
//               {/* Shift Card */}
//               <div className="selection-card shift-card-side">
//                 <div className="card-header">
//                   <div className="card-icon shift-icon">🕐</div>
//                   <div className="card-title-group">
//                     <h3 className="card-title">Select Shift</h3>
//                     <p className="card-subtitle">Choose working shift</p>
//                   </div>
//                 </div>
//                 <div className="shift-options">
//                   {ShiftOptions.map((shift) => (
//                     <button
//                       key={shift}
//                       onClick={() => handleShiftSelect(shift)}
//                       className={`shift-btn ${selectedShift === shift ? 'selected' : ''}`}
//                     >
//                       Shift {shift}
//                     </button>
//                   ))}
//                 </div>
//               </div>

//               {/* Area Card */}
//               {selectedShift && (
//                 <div className="selection-card area-card-side fade-in">
//                   <div className="card-header">
//                     <div className="card-icon area-icon">📍</div>
//                     <div className="card-title-group">
//                       <h3 className="card-title">Select Area</h3>
//                       <p className="card-subtitle">Choose production area</p>
//                     </div>
//                   </div>
//                   <div className="area-options">
//                     {AreaOptions.map((area) => (
//                       <button
//                         key={area}
//                         onClick={() => handleAreaSelect(area)}
//                         className={`area-btn ${selectedArea === area ? 'selected' : ''}`}
//                       >
//                         {area}
//                       </button>
//                     ))}
//                   </div>
//                 </div>
//               )}
//             </div>
//           )}

//           {/* Project Selection */}
//           {selectedArea && selectedShift && (
//             <div ref={projectRef} className="selection-card project-card fade-in">
//               <div className="card-header">
//                 <div className="card-icon project-icon">📋</div>
//                 <div className="card-title-group">
//                   <h3 className="card-title">Select Project</h3>
//                   <p className="card-subtitle">Choose your project</p>
//                 </div>
//               </div>
              
//               {loading && !projects.length ? (
//                 <div className="loading-state">
//                   <div className="spinner"></div>
//                   <span>Loading projects...</span>
//                 </div>
//               ) : (
//                 <>
//                   {projects.length > 3 && (
//                     <div className="search-box">
//                       <span className="search-icon">🔍</span>
//                       <input
//                         type="text"
//                         placeholder="Search projects..."
//                         value={searchTerm}
//                         onChange={(e) => setSearchTerm(e.target.value)}
//                         className="search-input"
//                       />
//                     </div>
//                   )}
//                   <div className="project-grid">
//                     {filteredProjects.length > 0 ? (
//                       filteredProjects.map((project, index) => (
//                         <button
//                           key={index}
//                           onClick={() => handleProjectSelect(project)}
//                           className={`project-item ${selectedProject === project ? 'selected' : ''}`}
//                         >
//                           <div className="project-name">
//                             {getDisplayValue(project, 'projName', 'projectName', 'name')}
//                           </div>
//                           <div className="project-footer">
//                             <div className="project-code">
//                               {getDisplayValue(project, 'projCode', 'projectCode', 'code')}
//                             </div>
//                             <div className={`project-status-indicator ${project.hasProduction ? 'active' : 'inactive'}`}
//                                  title={project.hasProduction ? 'Active Production' : 'No Production'}>
//                             </div>
//                           </div>
//                         </button>
//                       ))
//                     ) : (
//                       <div className="empty-state">
//                         <span className="empty-icon">📋</span>
//                         <p>No projects found</p>
//                       </div>
//                     )}
//                   </div>
//                 </>
//               )}
//             </div>
//           )}

//           {/* Line Selection */}
//           {selectedProject && (
//             <div ref={lineRef} className="selection-card line-card fade-in">
//               <div className="card-header">
//                 <div className="card-icon line-icon">⚙️</div>
//                 <div className="card-title-group">
//                   <h3 className="card-title">Select Production Line</h3>
//                   <p className="card-subtitle">Choose your production line</p>
//                 </div>
//               </div>

//               {loading && !lines.length ? (
//                 <div className="loading-state">
//                   <div className="spinner"></div>
//                   <span>Loading lines...</span>
//                 </div>
//               ) : (
//                 <>
//                   {lines.length > 3 && (
//                     <div className="search-box">
//                       <span className="search-icon">🔍</span>
//                       <input
//                         type="text"
//                         placeholder="Search lines..."
//                         value={lineSearchTerm}
//                         onChange={(e) => setLineSearchTerm(e.target.value)}
//                         className="search-input"
//                       />
//                     </div>
//                   )}
//                   <div className="line-grid">
//                     {filteredLines.length > 0 ? (
//                       filteredLines.map((line) => (
//                         <button
//                           key={line.userId}
//                           onClick={() => handleLineSelect(line)}
//                           className={`line-item ${selectedLine?.userId === line.userId ? 'selected' : ''}`}
//                         >
//                           <div className="line-header">
//                             <div className="line-info">
//                               <div className="line-number">{line.lineNo}</div>
//                               {/* <div className="line-user">{line.userName}</div> */}
//                             </div>
//                             <div className="line-status-dot"></div>
//                           </div>
//                           <div className="line-stats">
//                             <div className="line-stat">
//                               <div className="stat-label">USER ID</div>
//                               <div className="stat-value user-id">{line.userId}</div>
//                             </div>
//                             <div className="line-stat">
//                               <div className="stat-label">PRODUCTION</div>
//                               <div className="stat-value production">
//                                 <span className="trend-icon">📈</span>
//                                 {line.totalProduction}
//                               </div>
//                             </div>
//                           </div>
//                           <div className="line-sync">
//                             <span className="sync-icon">🕐</span>
//                             {line.lastSynced}
//                           </div>
//                         </button>
//                       ))
//                     ) : (
//                       <div className="empty-state">
//                         <span className="empty-icon">⚙️</span>
//                         <p>No lines available</p>
//                       </div>
//                     )}
//                   </div>
//                 </>
//               )}
//             </div>
//           )}

//           {/* View Dashboard Button - Shows after line is selected */}
//           {selectedLine && (
//             <div className="dashboard-action-card fade-in">
//               <button onClick={handleFind} className="dashboard-btn-large" disabled={!!dateError}>
//                 <span className="btn-icon">⚡</span>
//                 View Dashboard
//                 <span className="btn-arrow">→</span>
//               </button>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default HomePage;